import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Undo2 } from 'lucide-react';
import { getAutocompleteTags, getTag, getTagList, moveTag } from '@/services/tags.service';
import { useAuthenticatedQueryFn } from '@/hooks/useAuthenticatedQuery';
import { ToastHighlight } from '@/components/ui/toast-highlight';
import { SortOrderOptions, SortOrderValues } from '@/types/sort';
import { useCommandContext } from '@/context/CommandContext';
import { updateAsset } from '@/services/asset.service';
import { useToast } from '@/components/ui/use-toast';
import { ToastAction } from '@/components/ui/toast';
import { QueryKey } from '@/lib/queryKeys';
import { useTranslations } from 'next-intl';

export const useAutocompleteTags = (q?: any, options?: object) => {
  const getAutocompleteTagsFn = useAuthenticatedQueryFn(getAutocompleteTags);

  const {
    data: tags,
    isLoading: areTagsLoading,
    isPending: areTagsPending,
    isFetching: areTagsFetching,
    error: tagsError,
    refetch: refetchTags,
  } = useQuery({
    queryKey: [QueryKey.Tags, q],
    queryFn: () => getAutocompleteTagsFn(q),
    placeholderData: keepPreviousData,
    ...options,
  });

  return {
    tags,
    refetchTags,
    areTagsFetching,
    areTagsLoading,
    areTagsPending,
    tagsError,
  };
};

export const useTagList = (
  params: {
    pagination: { limit: number; offset: number };
    sort: { order: string; value: string };
    queryString: string;
  },
  options?: object,
) => {
  const tagListQueryFn = useAuthenticatedQueryFn(getTagList);

  const {
    data: tagList = {
      tags: [],
      pagination: { count: 0, limit: 0, offset: 0 },
      sort: { order: SortOrderOptions.ASC, value: SortOrderValues.CREATED_AT },
    },
    isFetching,
    isPending,
    isFetched,
    error,
    refetch,
  } = useQuery({
    queryKey: [QueryKey.TagList, params.pagination, params.queryString, params.sort],
    queryFn: () => tagListQueryFn(params),
    placeholderData: keepPreviousData,
    ...options,
  });

  return {
    data: tagList.tags,
    pagination: tagList.pagination,
    isFetching,
    isPending,
    isFetched,
    refetch,
    error,
  };
};

export const useTag = (id: string, options: object, form: any) => {
  const getTagQueryFn = useAuthenticatedQueryFn(getTag);

  const {
    data: tag = [],
    isPending: tagIsPending,
    isFetching: tagIsFetching,
    error: tagError,
    refetch: refetchTag,
  } = useQuery({
    queryKey: [QueryKey.Tag, id],
    queryFn: async () => {
      const data = await getTagQueryFn({ asset_id: id });
      form?.reset(data);
      return data;
    },
    ...options,
  });

  return {
    tag,
    tagIsPending,
    refetchTag,
    tagIsFetching,
    tagError,
  };
};

export const useTagMutation = () => {
  const updateAssetWithAuth = useAuthenticatedQueryFn(updateAsset);
  const queryClient = useQueryClient();
  const tCommon = useTranslations('common');
  const tToasts = useTranslations('toasts');

  const { toast } = useToast();
  const { undo } = useCommandContext();

  const { mutateAsync: updateAssetMutation } = useMutation({
    mutationFn: updateAssetWithAuth,
    onSettled: (response, error, params) => {
      if (params.context === 'clear') {
        toast({
          title: tToasts('updateAssetMutation.clearTitle'),
          description: tToasts('updateAssetMutation.clearDescription'),
          action: (
            <ToastAction onClick={undo} altText={tToasts('undoTagChange')} icon={Undo2}>
              {tCommon('undo')}
            </ToastAction>
          ),
        });
      } else {
        const assetName = response?.name ?? tCommon('asset');

        toast({
          title: tToasts('updateAssetMutation.updateTitle'),
          descriptionClassName: 'flex min-w-0 whitespace-pre',
          description: tToasts.rich('updateAssetMutation.updateDescription', {
            assetName,
            highlight: (chunks) => <ToastHighlight chunks={chunks} leading />,
          }),
        });
      }

      void queryClient.invalidateQueries({ queryKey: [QueryKey.Asset, response?.id ?? undefined] });
    },
    onError: () => {
      toast({
        title: tCommon('error'),
        description: tToasts('updateAssetMutation.errorDescription'),
      });
    },
  });

  return {
    updateAssetMutation,
  };
};

export const useMoveTagMutation = () => {
  const moveTagWithAuth = useAuthenticatedQueryFn(moveTag);
  const queryClient = useQueryClient();
  const tCommon = useTranslations('common');
  const tToasts = useTranslations('toasts');

  const { toast } = useToast();

  const moveTagMutation = useMutation({
    mutationFn: moveTagWithAuth,
    onSuccess: (result) => {
      const movedTag = result.moved[0].tags[0].name;
      const newParentTag = result.moved[0].parentTag;

      toast({
        title: tToasts(newParentTag ? 'moveTagMutation.title' : 'removeParentTagMutation.title'),
        descriptionClassName: 'flex min-w-0 whitespace-pre',
        description: newParentTag
          ? tToasts.rich('moveTagMutation.description', {
              movedTag,
              parentTag: newParentTag.name,
              highlight: (chunks) => <ToastHighlight chunks={chunks} />,
            })
          : tToasts.rich('removeParentTagMutation.description', {
              movedTag,
              highlight: (chunks) => <ToastHighlight chunks={chunks} />,
            }),
      });
    },
    onError: (err: any) =>
      toast({
        title: tCommon('error'),
        description: err ? err.error : tToasts('moveTagMutation.errorDescription'),
      }),
    onSettled: (_data, _error, variables: any) => {
      void queryClient.invalidateQueries({ queryKey: [QueryKey.TagList] });

      const movedTagId = variables?.move?.[0]?.tags?.[0];
      if (movedTagId) {
        void queryClient.invalidateQueries({ queryKey: [QueryKey.Tag, movedTagId] });
      }
    },
  });

  return {
    moveTagMutation,
  };
};
